import { AiDrawer } from '@aigc/components';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlayRef } from '@aigc/overlay-hook';
import { InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Empty,
  Form,
  Image,
  Mentions,
  Skeleton,
  Typography,
  Upload,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useForm, Watch } from 'react-hook-form';
import { v7 as uuid } from 'uuid';
import { object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import { createBeforeUpload, createUploadValidator } from '../../file';
import { usePreviewFn } from '../../file/hooks';
import { fileSchema } from '../../file/schema';
import { toFileInputDtoList, toFileListFormValues } from '../../file/utils';
import { useSetReferenceImagePageData } from '../../reference-image/store';
import {
  getBaseTaskListQueryKey,
  getOneTaskQueryOptions,
} from '../../task/api';
import { TaskStatus } from '../../task/components/task-status';
import { TaskStatusCode, type VideoReplicationTaskDto } from '../../task/model';
import { buildImageMentionOptions } from '../../video-model/image-mention-utils';
import { replicateVideo } from '../api';
import {
  useResetVideoReplicationResultDraft,
  useVideoReplicationResultDraft,
} from '../store';
import {
  buildVideoReplicationPrompt,
  buildVideoReplicationVideoPageData,
  MAX_VIDEO_REPLICATION_REFERENCE_IMAGES,
} from '../utils';

const result_form_schema = object({
  reference_images: fileSchema
    .min(1, '请至少上传一张参考图片')
    .max(
      MAX_VIDEO_REPLICATION_REFERENCE_IMAGES,
      `参考图片最多上传 ${MAX_VIDEO_REPLICATION_REFERENCE_IMAGES} 张`,
    )
    .required(),
  script: string().trim().required('请输入复刻脚本'),
});

export type VideoReplicationDrawerCloseReason = 'manual' | 'navigate';

export function VideoReplicationDrawer(props: {
  taskId: string;
  onTaskIdChange: (taskId: string) => void;
}) {
  const { message, notification } = App.useApp();
  const overlay_ref = useOverlayRef<VideoReplicationDrawerCloseReason>();
  const query_client = useQueryClient();
  const navigate = useNavigate();
  const on_preview = usePreviewFn();
  const [current_task_id, set_current_task_id] = useState(props.taskId);
  const [draft, setDraft] = useVideoReplicationResultDraft(current_task_id);
  const resetDraft = useResetVideoReplicationResultDraft(current_task_id);
  const initializedTaskIdRef = useRef<string | undefined>(undefined);
  const setReferenceImagePageData = useSetReferenceImagePageData(false);
  const setLongReferenceImagePageData = useSetReferenceImagePageData(true);
  const retry_mutation = useMutation(replicateVideo);
  const form = useForm({
    defaultValues: {
      reference_images: [],
      script: '',
    },
    resolver: yupResolver(result_form_schema),
  });
  const task_query = useQuery({
    ...getOneTaskQueryOptions<VideoReplicationTaskDto>(current_task_id),
    refetchInterval: (query) =>
      TaskStatus.isSettled(query.state.data?.status) ? false : 5000,
  });
  const task = task_query.data;
  const input = task?.generate_data;
  const result = task?.result;

  const accept_image = useAccept(['.jpg', '.jpeg', '.png', '.webp']);
  useAutoPaste(`#replicationResultImages-${current_task_id}`, {
    filter: accept_image.filter,
  });

  useEffect(() => {
    if (initializedTaskIdRef.current === current_task_id) {
      return;
    }

    if (!result) {
      return;
    }

    form.reset({
      reference_images: toFileListFormValues(
        draft.reference_images ?? input?.reference_images ?? [],
      ),
      script: draft.script ?? buildVideoReplicationPrompt(result),
    });
    initializedTaskIdRef.current = current_task_id;
  }, [current_task_id, draft, form, input?.reference_images, result]);

  useEffect(() => {
    return form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        setDraft({
          script: values.script,
          reference_images: toFileInputDtoList(values.reference_images),
        });
      },
    });
  }, [form, setDraft]);

  useEffect(() => {
    if (TaskStatus.isSettled(task?.status)) {
      query_client.invalidateQueries({
        queryKey: getBaseTaskListQueryKey(),
      });
    }
  }, [query_client, task?.status]);

  const handleRetry = async () => {
    if (!input || retry_mutation.isPending) {
      return;
    }

    const new_task = await retry_mutation.mutateAsync(input);
    await query_client.invalidateQueries({
      queryKey: getBaseTaskListQueryKey(),
    });
    set_current_task_id(new_task.id);
    props.onTaskIdChange(new_task.id);
    message.success('视频复刻任务已重新创建');
  };

  const handleGenerateVideo = form.handleSubmit((form_values) => {
    if (!result) {
      return;
    }

    const pageData = buildVideoReplicationVideoPageData({
      id: uuid(),
      result,
      prompt: form_values.script,
      referenceImages: form_values.reference_images,
    });

    if (pageData.file_list.length > MAX_VIDEO_REPLICATION_REFERENCE_IMAGES) {
      message.error(
        `参考图片最多上传 ${MAX_VIDEO_REPLICATION_REFERENCE_IMAGES} 张`,
      );
      return;
    }

    setDraft({
      script: form_values.script,
      reference_images: pageData.file_list,
    });

    if (pageData.duration > 15) {
      setLongReferenceImagePageData(pageData);
      navigate({ to: '/video/long/reference-create' });
    } else {
      setReferenceImagePageData(pageData);
      navigate({ to: '/video/reference-create' });
    }

    overlay_ref.close('navigate');
  });

  return (
    <AiDrawer
      title={`视频复刻 #${current_task_id}`}
      size="70%"
      mask={{ closable: false }}
      onClose={() => {
        resetDraft();
        overlay_ref.close('manual');
      }}
      footer={
        TaskStatus.isSucceeded(task?.status) &&
        result && (
          <div className="flex justify-end">
            <Button
              variant="solid"
              color="primary"
              onClick={handleGenerateVideo}
            >
              生成视频
            </Button>
          </div>
        )
      }
    >
      {task_query.isLoading ? (
        <Skeleton active />
      ) : task_query.isError || !task ? (
        <Alert
          type="error"
          showIcon
          title="任务详情加载失败"
          description="请检查网络连接后重试"
          action={
            <Button
              icon={<ReloadOutlined />}
              loading={task_query.isFetching}
              onClick={() => task_query.refetch()}
            >
              重新加载
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <TaskStatus task={task} />
            {!TaskStatus.isSettled(task.status) && (
              <span className="text-sm text-primary-500">
                任务进行中，请稍候。你也可以关闭弹窗，稍后从历史任务重新查看。
              </span>
            )}
          </div>

          <Descriptions size="small" bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="爆款视频" span={2}>
              {input?.video.url ? (
                <video
                  className="max-h-[240px] max-w-full rounded"
                  src={input.video.url}
                  controls
                  playsInline
                />
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="其他要求" span={2}>
              <span className="whitespace-pre-wrap">
                {input?.extra_prompt || '-'}
              </span>
            </Descriptions.Item>
            {!TaskStatus.isSettled(task.status) &&
              !!input?.reference_images.length && (
                <Descriptions.Item label="参考图片" span={2}>
                  <Image.PreviewGroup>
                    <div className="flex flex-wrap gap-3">
                      {input.reference_images.map((image, index) => (
                        <Image
                          key={`(${index})${image.name}`}
                          width={96}
                          height={96}
                          className="rounded object-cover"
                          src={image.url}
                          alt={`参考图片 ${image.name}`}
                        />
                      ))}
                    </div>
                  </Image.PreviewGroup>
                </Descriptions.Item>
              )}
            {result && (
              <>
                <Descriptions.Item label="产品名称">
                  {result.product_name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="视频时长">
                  {result.duration} 秒
                </Descriptions.Item>
                <Descriptions.Item label="视频比例">
                  {result.aspect_ratio || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="投放区域">
                  {result.target_region || '-'}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>

          {task.status === TaskStatusCode.任务失败 ? (
            <div className="space-y-4">
              <Alert
                type="error"
                showIcon
                title="视频复刻失败"
                description={task.error || '任务执行失败，请重试'}
              />
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={retry_mutation.isPending}
                disabled={!input}
                onClick={handleRetry}
              >
                重试
              </Button>
            </div>
          ) : task.status === TaskStatusCode.任务成功 ? (
            result ? (
              <Form layout="vertical">
                <FormControl
                  control={form.control}
                  name="reference_images"
                  label="参考图片"
                  required
                  exact
                  render={({ field }) => (
                    <Upload.Dragger
                      id={`replicationResultImages-${current_task_id}`}
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      classNames={{ list: 'mt-2' }}
                      listType="picture-card"
                      fileList={field.value}
                      multiple
                      maxCount={MAX_VIDEO_REPLICATION_REFERENCE_IMAGES}
                      accept=".jpg,.jpeg,.png,.webp"
                      beforeUpload={createBeforeUpload({
                        validator: createUploadValidator({
                          accept: '.jpg,.jpeg,.png,.webp',
                        }),
                        onError: ({ title, message }) => {
                          notification.error({ title, description: message });
                        },
                      })}
                      onPreview={on_preview}
                      onChange={(info) => {
                        if (info.file.status === 'error') {
                          notification.error({
                            title: '错误',
                            description: `图片 ${info.file.name} 上传失败，请重试`,
                          });
                        }

                        field.onChange(
                          info.fileList.map((item) => {
                            if (item.response) {
                              item.url = item.response.file_url;
                              item.thumbUrl = item.response.file_url;
                            }

                            return item;
                          }),
                        );
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">
                        点击、拖放或
                        <span className="text-blue-500">粘贴</span>继续上传图片
                      </p>
                      <p className="ant-upload-hint">
                        原参考图与新增图片合计最多{' '}
                        {MAX_VIDEO_REPLICATION_REFERENCE_IMAGES} 张
                      </p>
                    </Upload.Dragger>
                  )}
                />
                <Watch
                  control={form.control}
                  name="reference_images"
                  render={(referenceImageFiles) => (
                    <FormControl
                      control={form.control}
                      name="script"
                      label={
                        <>
                          复刻脚本， 支持通过输入
                          <Typography.Text keyboard>@</Typography.Text>
                          字符来引用图片，例如
                          <Typography.Text code>@图片1</Typography.Text>
                        </>
                      }
                      required
                      render={({ field }) => (
                        <Mentions
                          {...field}
                          options={buildImageMentionOptions(
                            referenceImageFiles.map((file) => ({
                              disabled: file.status !== 'done',
                            })),
                          )}
                          autoSize={{ minRows: 12, maxRows: 30 }}
                          placeholder="请输入复刻脚本，可通过 @ 引用参考图"
                        />
                      )}
                    />
                  )}
                />
              </Form>
            ) : (
              <Empty description="视频复刻结果为空" />
            )
          ) : null}
        </div>
      )}
    </AiDrawer>
  );
}
